import { APP_SETTINGS } from "../../shared/app_settings";
import { CHANNELS } from "../../shared/channels";
import { ClientCredit, ClientOc, ClientProfile, CommType, JobLink, ReferenceEntry, StudioJob, StudioState, TaskEntry, TaskSnapshot, UserSettings } from "../../shared/types";
import { CURRENCIES, ClientBalance, clientBalances, commonSymbol, jobSign, commsStats, currencySign, formatMoney, jobClient, jobOwed, jobPaid, jobPrice, monthLabel, paidDay, parseAmount } from "../../shared/money";
import { cachedSettings, rememberSettings } from "../settings_cache";
import { call, listen, mediaSrc } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { onViewShown } from "../navigation";
import { buzz, flash } from "../tweens";
import { shrinkImage } from "../utils";
import { fillSharedBox, onSharedChange, readSharedAll, sharedHead } from "../shares";
import { drawBars, drawColumns, drawSplit, setGauge } from "../charts";
import { canUse } from "../me";
import { loadRefs } from "../refs_store";
import { locale, t } from "../i18n";
import { openTaskEditor } from "./task_editor";
import { showView } from "../navigation";
import { showStudioTip } from "../minipi_card";
import { Kanban, buildArmedTrash, crunchCard, createKanban, openColumnPicker } from "../kanban";
import { buildCommTile, commColumns, commSign, loadCommSign } from "../commission_card";

// the timer runs off a wall clock end time so a sleeping phone does not lose minutes
const RING = 326.7;
let focusMinutes = 25;
let mode: "focus" | "break" = "focus";
let total = focusMinutes * 60;
let remaining = total;
let endAt = 0;
let ticking = 0;

function clock(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const tail = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return hours ? `${hours}:${tail}` : tail;
}

// what you type on the dial: 40 is minutes, 1h30 or 1:30:00 is hours, 12:30 or 12.30 is minutes and seconds
function parseClock(text: string): number | null {
  const raw = text.trim().toLowerCase().replace(/\s+/g, "");
  if (!raw) return null;
  let seconds: number;
  const hm = raw.match(/^(?:(\d+(?:[.,]\d+)?)h)?(?:(\d+)m?)?$/);
  if (raw.includes("h") && hm) {
    seconds = Number((hm[1] || "0").replace(",", ".")) * 3600 + Number(hm[2] || 0) * 60;
  } else if (/^\d+m?$/.test(raw)) {
    seconds = Number(raw.replace("m", "")) * 60;
  } else if (/^\d+([:.,]\d{1,2}){1,2}$/.test(raw)) {
    const parts = raw.split(/[:.,]/).map(Number);
    if (parts.slice(1).some((part) => part > 59)) return null;
    seconds = parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2] : parts[0] * 60 + parts[1];
  } else {
    return null;
  }
  seconds = Math.round(seconds);
  return seconds >= 1 && seconds <= 24 * 3600 ? seconds : null;
}

function blocksKey(): string {
  return `studio-blocks-${new Date().toISOString().slice(0, 10)}`;
}

function blocksToday(): number {
  try {
    return Number(localStorage.getItem(blocksKey()) || 0);
  } catch {
    return 0;
  }
}

// the home timer widget reads and drives the same timer
const timerListeners: Array<() => void> = [];
export function onTimer(listener: () => void): void {
  timerListeners.push(listener);
}
export function timerView(): { mode: string; clock: string; running: boolean; blocks: number; share: number } {
  return { mode, clock: clock(Math.max(0, remaining)), running: Boolean(ticking), blocks: blocksToday(), share: total ? Math.max(0, remaining) / total : 0 };
}
export function timerToggle(): void {
  if (ticking) pauseTimer();
  else startTimer();
}
export function timerReset(): void {
  resetTimer();
}

function paintTimer(): void {
  timerListeners.forEach((listener) => listener());
  byId("studio-timer").textContent = clock(Math.max(0, remaining));
  byId("studio-timer-mode").textContent = t(mode);
  byId("studio-ring").style.strokeDashoffset = String(RING * (1 - Math.max(0, remaining) / total));
  const blocks = blocksToday();
  byId("studio-timer-count").textContent = blocks === 1 ? t("1 block today") : t("{n} blocks today", { n: blocks });
  document.querySelector(".timer-deck")?.classList.toggle("running", Boolean(ticking));
  const start = byId("studio-timer-start");
  start.querySelector("span")!.textContent = ticking ? t("running") : remaining < total ? t("resume") : t("start");
}

function alarm(): void {
  buzz(300);
  const deck = document.querySelector(".timer-deck");
  deck?.classList.remove("done");
  void (deck as HTMLElement | null)?.offsetWidth;
  deck?.classList.add("done");
  flashToast(mode === "focus" ? t("back to drawing") : t("break time, stretch your hand"));
  try {
    if ("Notification" in window && Notification.permission === "granted") new Notification("ChiX", { body: mode === "focus" ? t("focus block started") : t("take a break") });
  } catch {
    // some browsers refuse notifications from pages
  }
}

function tick(): void {
  remaining = Math.round((endAt - Date.now()) / 1000);
  if (remaining <= 0) {
    if (mode === "focus") {
      try {
        localStorage.setItem(blocksKey(), String(blocksToday() + 1));
      } catch {
        // private mode
      }
    }
    mode = mode === "focus" ? "break" : "focus";
    total = (mode === "focus" ? focusMinutes : Math.max(5, Math.round(focusMinutes / 5))) * 60;
    remaining = total;
    endAt = Date.now() + total * 1000;
    call(CHANNELS.journalAdd, `studio ${mode} started`);
    alarm();
  }
  paintTimer();
}

function startTimer(): void {
  if (ticking) return;
  endAt = Date.now() + remaining * 1000;
  ticking = window.setInterval(tick, 500);
  if ("Notification" in window && Notification.permission === "default") void Notification.requestPermission().catch(() => undefined);
  paintTimer();
}

function pauseTimer(): void {
  window.clearInterval(ticking);
  ticking = 0;
  paintTimer();
}

function resetTimer(): void {
  pauseTimer();
  mode = "focus";
  total = focusMinutes * 60;
  remaining = total;
  paintTimer();
}

// typing a time sets what is left on the dial, running or paused; in focus it also becomes the block length
function setTime(seconds: number): void {
  if (mode === "focus") {
    focusMinutes = seconds / 60;
    byId("studio-presets").querySelectorAll<HTMLElement>(".seg-option").forEach((item) => item.classList.toggle("active", Number(item.dataset.minutes) * 60 === seconds));
  }
  total = seconds;
  remaining = seconds;
  if (ticking) endAt = Date.now() + remaining * 1000;
  paintTimer();
}

function editTime(): void {
  const face = byId("studio-timer");
  const field = byId<HTMLInputElement>("studio-timer-edit");
  const shown = clock(Math.max(0, remaining));
  let done = false;
  const close = (keep: boolean) => {
    if (done) return;
    done = true;
    field.removeEventListener("blur", onBlur);
    field.removeEventListener("keydown", onKey);
    if (keep && field.value.trim() !== shown) {
      const seconds = parseClock(field.value);
      if (seconds === null) flashToast(t("type minutes, like 40 or 1h30"));
      else setTime(seconds);
    }
    field.hidden = true;
    face.hidden = false;
  };
  const onBlur = () => close(true);
  const onKey = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      close(true);
    } else if (event.key === "Escape") {
      close(false);
    }
  };
  field.value = shown;
  face.hidden = true;
  field.hidden = false;
  field.addEventListener("blur", onBlur);
  field.addEventListener("keydown", onKey);
  field.focus();
  field.select();
}

let lastState: StudioState = { colors: [], prompts: [], jobs: [], types: [], credits: [], clients: [] };

// the pictures in the form: ones the commission already has and new ones picked, each can be dropped
let previewUrls: string[] = [];
function paintJobPreview(): void {
  previewUrls.forEach((url) => URL.revokeObjectURL(url));
  previewUrls = [];
  const tile = (src: string, drop: () => void) => {
    const wrap = document.createElement("div");
    wrap.className = "job-preview-pic";
    const image = document.createElement("img");
    image.src = src;
    image.alt = "";
    const key = document.createElement("button");
    key.type = "button";
    key.setAttribute("aria-label", t("drop this picture"));
    key.textContent = "×";
    key.addEventListener("click", () => {
      drop();
      paintJobPreview();
    });
    wrap.append(image, key);
    return wrap;
  };
  const kept = keptPics.map((pic) => tile(mediaSrc(pic.url), () => {
    keptPics = keptPics.filter((item) => item !== pic);
  }));
  const fresh = newPics.map((file) => {
    const url = URL.createObjectURL(file);
    previewUrls.push(url);
    return tile(url, () => {
      newPics = newPics.filter((item) => item !== file);
    });
  });
  byId("studio-job-preview").replaceChildren(...kept, ...fresh);
}

function resetJobForm(): void {
  editingJob = null;
  keptPics = [];
  newPics = [];
  byId<HTMLFormElement>("studio-job-form").reset();
  byId("studio-job-fold-title").textContent = t("new commission");
  byId("studio-job-save").textContent = t("add");
  byId("studio-job-cancel").hidden = true;
  paintJobPreview();
  document.querySelectorAll(".job-card.editing").forEach((card) => card.classList.remove("editing"));
}

// the tasks board sends you here to edit a commission: show the comms pane, then open it once it has loaded
let editWanted = "";
export function openCommission(id: string): void {
  editWanted = id;
  showView("studio", "jobs");
}

function startJobEdit(job: StudioJob): void {
  editingJob = job;
  keptPics = job.images.map((name, at) => ({ name, url: job.imageUrls?.[at] || "" }));
  newPics = [];
  byId<HTMLInputElement>("studio-job-name").value = job.name;
  byId<HTMLInputElement>("studio-job-client").value = job.client;
  byId<HTMLInputElement>("studio-job-amount").value = job.amount;
  byId<HTMLSelectElement>("studio-job-currency").value = job.currency || "";
  byId<HTMLSelectElement>("studio-job-type").value = lastState.types.some((type) => type.id === job.typeId) ? job.typeId : "";
  byId<HTMLInputElement>("studio-job-due").value = job.due;
  byId<HTMLSelectElement>("studio-job-status").value = job.status;
  byId<HTMLTextAreaElement>("studio-job-description").value = job.description;
  byId("studio-job-fold-title").textContent = t("edit {name}", { name: job.name });
  byId("studio-job-save").textContent = t("save");
  byId("studio-job-cancel").hidden = false;
  paintJobPreview();
  document.querySelectorAll(".job-card.editing").forEach((card) => card.classList.remove("editing"));
  const fold = byId<HTMLDetailsElement>("studio-job-fold");
  fold.open = true;
  fold.scrollIntoView({ behavior: "smooth", block: "start" });
}

function render(state: StudioState): void {
  lastState = state;
  renderList(byId("studio-colors"), state.colors, (hex) => {
    const swatch = fillFields(cloneTemplate("template-swatch"), { hex });
    swatch.style.background = hex;
    let held = 0;
    let tossed = false;
    const toss = async () => {
      tossed = true;
      buzz(20);
      if (confirm(t("toss {hex}?", { hex }))) render(await call(CHANNELS.studioRemoveColor, hex));
    };
    swatch.addEventListener("pointerdown", () => {
      tossed = false;
      held = window.setTimeout(() => void toss(), 600);
    });
    swatch.addEventListener("pointerup", () => window.clearTimeout(held));
    swatch.addEventListener("pointerleave", () => window.clearTimeout(held));
    swatch.addEventListener("click", async () => {
      if (tossed) return;
      await navigator.clipboard.writeText(hex).catch(() => undefined);
      flash(swatch);
      flashToast(t("copied {hex}", { hex }));
    });
    swatch.addEventListener("contextmenu", (event) => {
      event.preventDefault();
      window.clearTimeout(held);
      if (!tossed) void toss();
    });
    return swatch;
  });

  renderList(byId("studio-prompts"), state.prompts, (card) => {
    const row = fillFields(cloneTemplate("template-prompt"), { title: card.title, text: card.text.slice(0, 160) });
    row.addEventListener("click", async () => {
      await navigator.clipboard.writeText(card.text).catch(() => undefined);
      flashToast(t("prompt copied"));
    });
    row.querySelector("[data-action='remove']")!.addEventListener("click", async (event) => {
      event.stopPropagation();
      render(await call(CHANNELS.studioRemovePrompt, card.id));
    });
    return row;
  });

  jobs = state.jobs;
  jobsView = state.view === "board" ? "board" : "list";
  paintJobsView();
  renderList(byId("studio-jobs"), state.jobs, (job) => jobCard(job));
  const clients = [...new Set(state.jobs.map((job) => job.client || "").filter(Boolean))];
  byId("studio-job-clients").replaceChildren(...clients.map((name) => Object.assign(document.createElement("option"), { value: name })));
  paintTypeSelect(state.types);
  if (jobsMode === "money") drawMoney(byId("jobs-pane-money"), state.jobs);
  if (jobsMode === "clients") drawClients(state);
  if (jobsMode === "rates") drawRates(state.types);
}

// ---------- the commissions board: the same columns as the statuses, dragged or moved with the picker ----------

let jobsView: "list" | "board" = "list";
let jobBoard: Kanban<StudioJob> | null = null;

function paintJobsView(): void {
  byId("jobs-view").querySelectorAll<HTMLElement>("[data-view]").forEach((button) => {
    const on = button.dataset.view === jobsView;
    button.classList.toggle("active", on);
    button.setAttribute("aria-pressed", String(on));
  });
  byId("studio-jobs").hidden = jobsView !== "list";
  byId("studio-board").hidden = jobsView !== "board";
  if (jobsView === "board") jobBoard ??= makeJobBoard();
  jobBoard?.paint(jobs.map((job) => ({ key: job.id, column: job.status || "sketch", data: job })));
}

function makeJobBoard(): Kanban<StudioJob> {
  return createKanban<StudioJob>({
    host: byId("studio-board"),
    columns: commColumns(),
    build: (card) => {
      const job = card.data;
      return buildCommTile({
        job,
        sign: mySign || commSign(jobs),
        open: () => startJobEdit(job),
        move: () => openColumnPicker(job.name, commColumns(), card.column, (next) => void moveJob(job, next)),
        trash: buildArmedTrash(t("delete commission"), true, () => void trashJob(job, byId("studio-board").querySelector<HTMLElement>(`[data-key="${CSS.escape(job.id)}"]`)))
      });
    },
    move: (card, to) => void moveJob(card.data, to),
    trash: (card, tile) => void trashJob(card.data, tile),
    trashMode: () => "arm"
  });
}

// a move shows at once and is saved after. payments and pictures stay as they are, and done does not mean paid
async function moveJob(job: StudioJob, status: string): Promise<void> {
  if (job.status === status) return;
  render({ ...lastState, jobs: lastState.jobs.map((item) => (item.id === job.id ? { ...item, status } : item)) });
  buzz(10);
  try {
    render(await call(CHANNELS.studioAddJob, { ...job, status, imageData: [] }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
    render(await call(CHANNELS.studioGet));
  }
}

async function trashJob(job: StudioJob, tile: HTMLElement | null): Promise<void> {
  if (tile) await crunchCard(tile);
  try {
    render(await call(CHANNELS.studioRemoveJob, job.id));
    buzz(12);
  } catch (error) {
    tile?.classList.remove("task-crunch");
    flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
  }
}

// the money sign for my own commissions: the one picked in setup, else the one most used
function sign(): string {
  return mySign || commonSymbol(jobs.map((job) => job.amount));
}

function paintTypeSelect(types: CommType[]): void {
  const select = byId<HTMLSelectElement>("studio-job-type");
  const keep = select.value;
  const none = Object.assign(document.createElement("option"), { value: "", textContent: t("no type") });
  select.replaceChildren(none, ...types.map((type) => Object.assign(document.createElement("option"), { value: type.id, textContent: type.price ? `${type.name} · ${formatMoney(type.price, sign())}` : type.name })));
  select.value = types.some((type) => type.id === keep) ? keep : "";
}

// ---------- rates ----------

let editingType: CommType | null = null;

function resetRateForm(): void {
  editingType = null;
  byId<HTMLFormElement>("studio-rate-form").reset();
  byId("studio-rate-fold-title").textContent = t("new type");
  byId("studio-rate-save").textContent = t("add");
  byId("studio-rate-cancel").hidden = true;
}

function drawRates(types: CommType[]): void {
  byId("studio-rate-starter").hidden = types.length > 0;
  renderList(byId("studio-rates"), types, (type) => {
    const row = fillFields(cloneTemplate("template-rate"), { name: type.name, note: type.note, price: type.price ? formatMoney(type.price, sign()) : t("no price") });
    row.addEventListener("click", (event) => {
      if ((event.target as Element).closest("button")) return;
      editingType = type;
      byId<HTMLInputElement>("studio-rate-name").value = type.name;
      byId<HTMLInputElement>("studio-rate-price").value = type.price ? String(type.price) : "";
      byId<HTMLInputElement>("studio-rate-note").value = type.note;
      byId("studio-rate-fold-title").textContent = t("edit {name}", { name: type.name });
      byId("studio-rate-save").textContent = t("save");
      byId("studio-rate-cancel").hidden = false;
      const fold = byId<HTMLDetailsElement>("studio-rate-fold");
      fold.open = true;
      fold.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    row.querySelector("[data-action='remove']")!.addEventListener("click", async () => {
      if (editingType?.id === type.id) resetRateForm();
      render(await call(CHANNELS.studioRemoveType, type.id));
    });
    return row;
  });
}

// ---------- clients: what they owe and the credit held for them ----------

function drawClients(state: StudioState): void {
  const symbol = sign();
  const rows = clientBalances(state.jobs, state.credits);
  const owed = rows.reduce((total, row) => total + row.owed, 0);
  const credit = rows.reduce((total, row) => total + row.credit, 0);
  setGauge(byId("jobs-meter-owed"), formatMoney(owed, symbol, true), owed / Math.max(1, owed + commsStats(state.jobs, APP_SETTINGS.jobStatuses, 12, new Date(), symbol).earnedYear));
  setGauge(byId("jobs-meter-credit"), formatMoney(credit, symbol, true), credit / Math.max(1, credit + owed));
  byId("jobs-clients").classList.toggle("empty-list", !rows.length);
  renderList(byId("jobs-clients"), rows, (row) => {
    const card = fillFields(cloneTemplate("template-client"), {
      name: row.client,
      meta: clientMeta(row, state),
      balance: ""
    });
    paintBalance(card.querySelector<HTMLElement>("[data-field='balance']")!, row, symbol);
    card.querySelector("[data-action='open']")!.addEventListener("click", () => {
      openClient = row.client.toLowerCase();
      drawClients(lastState);
      byId("client-sheet").scrollIntoView({ block: "start" });
    });
    return card;
  });
  // one client's sheet takes the place of the list
  const open = openClient ? rows.find((row) => row.client.toLowerCase() === openClient) : undefined;
  if (openClient && !open) openClient = null;
  const sheet = byId("client-sheet");
  sheet.hidden = !open;
  for (const part of [byId("jobs-meter-owed").parentElement!, byId("studio-credit-fold"), byId("jobs-clients"), byId("jobs-clients-hint")]) part.hidden = Boolean(open);
  if (open) sheet.replaceChildren(buildClientSheet(open, state));
  else sheet.replaceChildren();
}

function clientMeta(row: ClientBalance, state: StudioState): string {
  const profile = state.clients.find((item) => item.client.toLowerCase() === row.client.toLowerCase());
  const bits = [row.owed ? t("{n} open", { n: row.open }) : t("settled")];
  if (profile?.ocs.length) bits.push(t("{n} characters", { n: profile.ocs.length }));
  if (profile?.refIds.length) bits.push(t("{n} references", { n: profile.refIds.length }));
  return bits.join(" · ");
}

function paintBalance(target: HTMLElement, row: ClientBalance, symbol: string): void {
  const parts: string[] = [];
  if (row.owed) parts.push(t("{amount} owed", { amount: formatMoney(row.owed, symbol) }));
  if (row.credit) parts.push(t("{amount} credit", { amount: formatMoney(row.credit, symbol) }));
  target.textContent = parts.join(" · ") || t("even");
  target.classList.toggle("money-owed", row.owed > 0);
  target.classList.toggle("money-credit", row.owed === 0 && row.credit > 0);
}

// ---------- a client's sheet: notes, their own characters, references chi found for them ----------

// the client whose sheet is open, lowercase. null shows the list
let openClient: string | null = null;

function buildClientSheet(row: ClientBalance, state: StudioState): HTMLElement {
  const symbol = sign();
  const key = row.client.toLowerCase();
  const profile: ClientProfile = state.clients.find((item) => item.client.toLowerCase() === key) || { client: row.client, note: "", ocs: [], refIds: [], skipped: [] };
  const node = fillFields(cloneTemplate("template-client-sheet"), { name: row.client, balance: "" });
  paintBalance(node.querySelector<HTMLElement>("[data-field='balance']")!, row, symbol);
  node.querySelector("[data-action='back']")!.addEventListener("click", () => {
    openClient = null;
    drawClients(lastState);
  });
  node.querySelector("[data-action='credit']")!.addEventListener("click", () => {
    openClient = null;
    drawClients(lastState);
    byId<HTMLInputElement>("studio-credit-client").value = row.client;
    const fold = byId<HTMLDetailsElement>("studio-credit-fold");
    fold.open = true;
    fold.scrollIntoView({ behavior: "smooth", block: "start" });
    byId("studio-credit-amount").focus();
  });
  const fail = (error: unknown) => flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));

  // notes
  const note = node.querySelector<HTMLTextAreaElement>("[data-client-note]")!;
  note.value = profile.note;
  note.addEventListener("change", async () => {
    try {
      render(await call(CHANNELS.studioSaveClient, { client: row.client, note: note.value }));
    } catch (error) {
      fail(error);
    }
  });

  // credit history
  const mine = state.credits.filter((item) => item.client.toLowerCase() === key);
  node.querySelector<HTMLElement>("[data-history-panel]")!.hidden = !mine.length;
  node.querySelector<HTMLElement>("[data-history]")!.replaceChildren(...mine.slice().reverse().map((item) => creditRow(item, symbol)));

  wireClientOcs(node, row.client, profile, fail);
  wireClientRefs(node, row.client, profile, fail);
  return node;
}

function wireClientOcs(node: HTMLElement, client: string, profile: ClientProfile, fail: (error: unknown) => void): void {
  const list = node.querySelector<HTMLElement>("[data-ocs]")!;
  const form = node.querySelector<HTMLFormElement>("[data-oc-form]")!;
  const name = form.querySelector<HTMLInputElement>("[data-oc-name]")!;
  const look = form.querySelector<HTMLTextAreaElement>("[data-oc-look]")!;
  const pic = form.querySelector<HTMLInputElement>("[data-oc-pic]")!;
  const pics = form.querySelector<HTMLElement>("[data-oc-pics]")!;
  const remove = form.querySelector<HTMLButtonElement>("[data-oc-remove]")!;
  const describe = form.querySelector<HTMLButtonElement>("[data-oc-describe]")!;
  let editing = "";
  // pictures already on the character, and ones picked but not saved yet
  let kept: Array<{ name: string; url: string }> = [];
  let fresh: File[] = [];
  const paintPics = () => {
    const tiles = kept.map((item) => picTile(mediaSrc(item.url), () => { kept = kept.filter((other) => other !== item); paintPics(); }));
    fresh.forEach((file) => tiles.push(picTile(URL.createObjectURL(file), () => { fresh = fresh.filter((other) => other !== file); paintPics(); })));
    pics.replaceChildren(...tiles);
    const total = kept.length + fresh.length;
    pic.parentElement!.hidden = total >= 6;
    // chi describes the saved pictures, so the button waits for a character that has some
    describe.hidden = !editing || !profile.ocs.find((oc) => oc.id === editing)?.images.length;
  };
  const openForm = (oc?: ClientOc) => {
    editing = oc?.id || "";
    name.value = oc?.name || "";
    look.value = oc?.look || "";
    pic.value = "";
    kept = (oc?.images || []).map((file, at) => ({ name: file, url: oc?.imageUrls?.[at] || "" }));
    fresh = [];
    remove.hidden = !oc;
    form.hidden = false;
    paintPics();
    name.focus();
  };
  const tiles = profile.ocs.map((oc) => {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "client-oc";
    const art = document.createElement("span");
    art.className = "client-oc-art";
    if (oc.imageUrls?.[0]) {
      const img = document.createElement("img");
      img.src = mediaSrc(oc.imageUrls[0]);
      img.alt = "";
      img.loading = "lazy";
      art.append(img);
      if (oc.imageUrls.length > 1) {
        const more = document.createElement("em");
        more.textContent = `+${oc.imageUrls.length - 1}`;
        art.append(more);
      }
    } else {
      art.textContent = oc.name.slice(0, 1).toUpperCase();
    }
    const text = document.createElement("span");
    text.className = "client-oc-text";
    const title = document.createElement("b");
    title.textContent = oc.name;
    const desc = document.createElement("span");
    desc.textContent = oc.look;
    text.append(title, desc);
    tile.append(art, text);
    tile.addEventListener("click", () => openForm(oc));
    return tile;
  });
  const add = document.createElement("button");
  add.type = "button";
  add.className = "client-oc-add";
  add.textContent = t("add a character");
  add.addEventListener("click", () => openForm());
  list.replaceChildren(...tiles, add);
  pic.addEventListener("change", () => {
    fresh = [...fresh, ...Array.from(pic.files || [])].slice(0, Math.max(0, 6 - kept.length));
    pic.value = "";
    paintPics();
  });
  form.querySelector("[data-oc-cancel]")!.addEventListener("click", () => { form.hidden = true; });
  describe.addEventListener("click", async () => {
    describe.disabled = true;
    describe.textContent = t("chi is looking...");
    try {
      look.value = await call<string>(CHANNELS.studioDescribeOc, client, editing);
    } catch (error) {
      fail(error);
    } finally {
      describe.disabled = false;
      describe.textContent = t("let chi describe them");
    }
  });
  remove.addEventListener("click", async () => {
    try {
      render(await call(CHANNELS.studioRemoveClientOc, client, editing));
    } catch (error) {
      fail(error);
    }
  });
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      const imageData: string[] = [];
      for (const file of fresh) imageData.push(await shrinkImage(file, APP_SETTINGS.sheetImageMaxPx));
      render(await call(CHANNELS.studioSaveClientOc, client, { id: editing || undefined, name: name.value, look: look.value, images: kept.map((item) => item.name), imageData }));
    } catch (error) {
      fail(error);
    }
  });
}

// a small picture with a × that takes it off
function picTile(src: string, onRemove: () => void): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "client-oc-pic-tile";
  const img = document.createElement("img");
  img.src = src;
  img.alt = "";
  const drop = document.createElement("button");
  drop.type = "button";
  drop.textContent = "×";
  drop.setAttribute("aria-label", t("take off"));
  drop.addEventListener("click", onRemove);
  wrap.append(img, drop);
  return wrap;
}

function refTile(entry: ReferenceEntry, onClick: () => void, label: string, mark: string): HTMLButtonElement {
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "client-ref";
  tile.setAttribute("aria-label", label);
  const img = document.createElement("img");
  img.src = mediaSrc(entry.thumbUrl || entry.imageUrl || entry.fileUrl);
  img.alt = entry.caption || "";
  img.loading = "lazy";
  const badge = document.createElement("i");
  badge.textContent = mark;
  tile.append(img, badge);
  tile.addEventListener("click", onClick);
  return tile;
}

function wireClientRefs(node: HTMLElement, client: string, profile: ClientProfile, fail: (error: unknown) => void): void {
  const panel = node.querySelector<HTMLElement>("[data-refs-panel]")!;
  panel.hidden = !canUse("refs");
  if (panel.hidden) return;
  const save = async (patch: { refIds?: string[]; skipped?: string[] }) => {
    try {
      render(await call(CHANNELS.studioSaveClient, { client, ...patch }));
    } catch (error) {
      fail(error);
    }
  };
  node.querySelector<HTMLElement>("[data-field='refcount']")!.textContent = profile.refIds.length ? t("{n} saved", { n: profile.refIds.length }) : "";
  void loadRefs().then((all) => {
    const byRef = new Map(all.map((entry) => [entry.id, entry]));
    const kept = profile.refIds.map((id) => byRef.get(id)).filter((entry): entry is ReferenceEntry => Boolean(entry));
    node.querySelector<HTMLElement>("[data-refs]")!.replaceChildren(...kept.map((entry) => refTile(entry, () => void save({ refIds: profile.refIds.filter((id) => id !== entry.id) }), t("take off"), "×")));
  }).catch(() => undefined);
  // chi's picks from what they wrote about the characters and the notes
  const sugg = node.querySelector<HTMLElement>("[data-sugg]")!;
  void call<ReferenceEntry[]>(CHANNELS.studioSuggestRefs, client).then((found) => {
    if (!node.isConnected || !found.length) return;
    sugg.hidden = false;
    node.querySelector<HTMLElement>("[data-sugg-strip]")!.replaceChildren(...found.map((entry) => refTile(entry, () => void save({ refIds: [...profile.refIds, entry.id] }), t("add"), "+")));
    node.querySelector("[data-sugg-all]")!.addEventListener("click", () => void save({ refIds: [...profile.refIds, ...found.map((entry) => entry.id)] }));
    node.querySelector("[data-sugg-skip]")!.addEventListener("click", () => void save({ skipped: [...profile.skipped, ...found.map((entry) => entry.id)] }));
  }).catch(() => undefined);
}

function creditRow(item: ClientCredit, symbol: string): HTMLElement {
  const line = document.createElement("div");
  line.className = "job-pay-row";
  const text = document.createElement("span");
  text.textContent = [item.date, item.note].filter(Boolean).join(" · ");
  const amount = document.createElement("b");
  amount.textContent = `${item.amount > 0 ? "+" : ""}${formatMoney(item.amount, symbol)}`;
  amount.className = item.amount > 0 ? "money-credit" : "";
  line.append(text, amount);
  // credit tied to a payment goes when that payment is taken back
  if (!item.paymentId) {
    const drop = document.createElement("button");
    drop.type = "button";
    drop.textContent = "×";
    drop.setAttribute("aria-label", t("take this back"));
    drop.addEventListener("click", async () => render(await call(CHANNELS.studioRemoveCredit, item.id)));
    line.append(drop);
  }
  return line;
}

// ---------- payments on a commission card ----------

function clientCredit(job: StudioJob): number {
  return lastState.credits.filter((item) => item.client.toLowerCase() === jobClient(job).toLowerCase()).reduce((total, item) => total + item.amount, 0);
}

function paintMoney(row: HTMLElement, job: StudioJob): void {
  const symbol = jobSign(job, sign());
  const price = jobPrice(job);
  const paid = jobPaid(job);
  const money = row.querySelector<HTMLElement>("[data-money]")!;
  money.hidden = !(price && paid);
  if (!money.hidden) {
    money.querySelector<HTMLElement>(".led-track > i")!.style.width = `${Math.min(100, Math.round((paid / price) * 100))}%`;
    const owed = jobOwed(job);
    row.querySelector<HTMLElement>("[data-field='moneyline']")!.textContent = owed
      ? t("{paid} paid · {owed} still owed", { paid: formatMoney(paid, symbol), owed: formatMoney(owed, symbol) })
      : t("{paid} paid, settled", { paid: formatMoney(paid, symbol) });
  }
  const list = row.querySelector<HTMLElement>("[data-pay-list]")!;
  list.replaceChildren(...job.payments.map((payment) => {
    const line = document.createElement("div");
    line.className = "job-pay-row";
    const text = document.createElement("span");
    text.textContent = [payment.date, payment.credit ? t("from credit") : "", payment.note].filter(Boolean).join(" · ");
    const amount = document.createElement("b");
    amount.textContent = formatMoney(payment.amount, symbol);
    const drop = document.createElement("button");
    drop.type = "button";
    drop.textContent = "×";
    drop.setAttribute("aria-label", t("take this back"));
    drop.addEventListener("click", async () => render(await call(CHANNELS.studioRemovePayment, job.id, payment.id)));
    line.append(text, amount, drop);
    return line;
  }));
  const full = row.querySelector<HTMLButtonElement>("[data-pay-full]")!;
  full.hidden = jobPrice(job) <= 0 || jobOwed(job) <= 0;
  full.textContent = t("pay {amount} in full", { amount: formatMoney(jobOwed(job), symbol) });
  const credit = clientCredit(job);
  const useCredit = row.querySelector<HTMLButtonElement>("[data-pay-credit]")!;
  useCredit.hidden = credit <= 0 || jobOwed(job) <= 0;
  useCredit.textContent = t("use {amount} credit", { amount: formatMoney(Math.min(credit, jobOwed(job)), symbol) });
}

async function pay(job: StudioJob, amount: string | number, credit: boolean): Promise<void> {
  const value = parseAmount(amount);
  const over = value - jobOwed(job);
  // more than is owed: say where the rest goes
  const keepExtra = !credit && jobPrice(job) > 0 && over > 0.005 && confirm(t("{amount} is over what is owed. keep it as credit for {client}?", { amount: formatMoney(over, jobSign(job, sign())), client: jobClient(job) }));
  try {
    render(await call(CHANNELS.studioAddPayment, job.id, { amount: credit ? Math.min(clientCredit(job), jobOwed(job)) : value, credit, keepExtra }));
    flashToast(t("payment added"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
  }
}

let jobs: StudioJob[] = [];
// my picked money sign, empty on auto
let mySign = "";
// the commission the form is changing, and the pictures it keeps and adds
let editingJob: StudioJob | null = null;
let keptPics: Array<{ name: string; url: string }> = [];
let newPics: File[] = [];
let jobsMode: "list" | "money" | "clients" | "rates" = "list";
let sharedJobs: Array<{ id: string; name: string; data: StudioJob[] }> = [];

async function renderSharedJobs(): Promise<void> {
  sharedJobs = await readSharedAll<StudioJob[]>("comms", []);
  fillSharedBox(byId("studio-jobs-shared"), sharedJobs, t("commissions"), (job) => jobCard(job, true), t("no commissions"));
  paintSharedMoney();
}

// the money graphs for whoever shares their comms with me, under my own
function paintSharedMoney(): void {
  const box = byId("jobs-money-shared");
  box.hidden = !sharedJobs.length;
  box.replaceChildren(...sharedJobs.map((person) => {
    const section = document.createElement("div");
    section.className = "stack shared-box";
    const inner = document.createElement("div");
    inner.className = "stack";
    inner.innerHTML = `
      <div class="meters"><div class="meter" data-meter="month"><span class="meter-num">-</span><span class="meter-label">${t("this month")}</span></div><div class="meter" data-meter="year"><span class="meter-num">-</span><span class="meter-label">${t("this year")}</span></div><div class="meter" data-meter="waiting"><span class="meter-num">-</span><span class="meter-label">${t("to be paid")}</span></div></div>
      <section class="panel stack money-panel"><div class="section-head"><h2>${t("paid by month")}</h2></div><div data-chart="months"></div></section>
      <section class="panel stack money-panel"><div class="section-head"><h2>${t("by client")}</h2></div><div data-chart="clients"></div></section>`;
    section.append(sharedHead(person.name, t("comms money")), inner);
    drawMoney(inner, person.data);
    return section;
  }));
}

// a pane of money stats: three gauges, paid per month, per client, and where the open work stands
function drawMoney(pane: HTMLElement, list: StudioJob[]): void {
  const stats = commsStats(list, APP_SETTINGS.jobStatuses, 12, new Date(), list === jobs ? mySign : "");
  const money = (value: number) => formatMoney(value, stats.symbol);
  const short = (value: number) => formatMoney(value, stats.symbol, true);
  const best = Math.max(...stats.byMonth.map((month) => month.amount), 0);
  const meter = (name: string) => pane.querySelector<HTMLElement>(`#jobs-meter-${name}, [data-meter="${name}"]`);
  // needles: this month against the best of the last twelve, this year against all of last year
  const month = meter("month");
  if (month) setGauge(month, short(stats.earnedMonth), best ? stats.earnedMonth / best : 0);
  const year = meter("year");
  if (year) setGauge(year, short(stats.earnedYear), stats.earnedYear / Math.max(stats.earnedYear, stats.earnedLastYear, 1));
  const waiting = meter("waiting");
  if (waiting) setGauge(waiting, short(stats.waiting), stats.waiting / Math.max(1, stats.waiting + stats.earnedYear));

  const months = pane.querySelector<HTMLElement>("#jobs-chart-months, [data-chart='months']");
  if (months) {
    drawColumns(months, {
      labels: stats.byMonth.map((item) => monthLabel(item.month, false, locale())),
      details: stats.byMonth.map((item) => `${t("{n} paid", { n: item.count })} · ${monthLabel(item.month, true, locale())}`),
      series: [{ name: t("paid"), color: "var(--chi)", values: stats.byMonth.map((item) => item.amount) }],
      format: money,
      axis: short,
      current: stats.byMonth.length - 1
    });
  }
  const clients = pane.querySelector<HTMLElement>("#jobs-chart-clients, [data-chart='clients']");
  if (clients) {
    const top = stats.byClient.slice(0, 6);
    const rest = stats.byClient.slice(6);
    const rows = top.map((row) => ({ label: row.client, value: row.amount, note: t("{n} paid", { n: row.count }) }));
    if (rest.length) rows.push({ label: t("{n} others", { n: rest.length }), value: rest.reduce((sum, row) => sum + row.amount, 0), note: t("{n} paid", { n: rest.reduce((sum, row) => sum + row.count, 0) }) });
    drawBars(clients, rows, money, t("nothing paid yet"));
  }
  const status = pane.querySelector<HTMLElement>("#jobs-chart-status");
  if (status) {
    const colors: Record<string, string> = { sketch: "var(--ink-3)", wip: "var(--violet)", review: "var(--amber)", done: "var(--chi)" };
    drawSplit(status, stats.byStatus.map((row) => ({ label: t(row.status), value: row.amount, note: String(row.count), color: colors[row.status] })), short);
  }
}

function setJobsMode(mode: "list" | "money" | "clients" | "rates"): void {
  jobsMode = mode;
  byId("jobs-mode").querySelectorAll<HTMLElement>(".seg-option").forEach((item) => item.classList.toggle("active", item.dataset.mode === mode));
  for (const name of ["list", "money", "clients", "rates"]) byId(`jobs-pane-${name}`).hidden = mode !== name;
  if (mode === "money") {
    drawMoney(byId("jobs-pane-money"), jobs);
    paintSharedMoney();
  }
  if (mode === "clients") drawClients(lastState);
  if (mode === "rates") drawRates(lastState.types);
}

// plane and chix tasks to pin a commission to. asked once per open, the list moves slowly
let taskList: Promise<TaskEntry[]> | null = null;

function loadTasks(fresh = false): Promise<TaskEntry[]> {
  if (!taskList || fresh) taskList = call<TaskSnapshot>(CHANNELS.tasksList).then((snap) => snap.tasks).catch(() => []);
  return taskList;
}

async function saveLinks(job: StudioJob, links: JobLink[]): Promise<void> {
  render(await call(CHANNELS.studioAddJob, { ...job, links, imageData: [] }));
}

async function openLink(link: JobLink): Promise<void> {
  if (link.source === "chix") {
    const task = (await loadTasks()).find((item) => item.source === "chix" && item.id === link.id);
    if (task) return openTaskEditor(task);
    if (!link.url) {
      flashToast(t("that task is done or gone"));
      return;
    }
  }
  if (link.url) void call(CHANNELS.openExternal, link.url);
  else showView("tasks");
}

function linkChips(box: HTMLElement, job: StudioJob, readOnly: boolean): void {
  box.replaceChildren(...job.links.map((link) => {
    const chip = document.createElement("span");
    chip.className = "chip job-link";
    const badge = document.createElement("span");
    badge.className = `badge ${link.source}`;
    badge.textContent = link.source;
    const title = document.createElement("button");
    title.type = "button";
    title.className = "job-link-title";
    title.textContent = link.title;
    title.addEventListener("click", () => void openLink(link));
    chip.append(badge, title);
    if (!readOnly) {
      const drop = document.createElement("button");
      drop.type = "button";
      drop.className = "job-link-drop";
      drop.setAttribute("aria-label", t("unlink {title}", { title: link.title }));
      drop.textContent = "×";
      drop.addEventListener("click", () => void saveLinks(job, job.links.filter((item) => item !== link)));
      chip.append(drop);
    }
    return chip;
  }));
}

// the task finder that drops open under a card
async function fillLinker(row: HTMLElement, job: StudioJob): Promise<void> {
  const list = row.querySelector<HTMLElement>("[data-linker-list]")!;
  const search = row.querySelector<HTMLInputElement>("[data-linker-search]")!;
  const tasks = await loadTasks(true);
  const paint = () => {
    const words = search.value.trim().toLowerCase();
    const taken = new Set(job.links.map((link) => `${link.source}:${link.id}`));
    const hits = tasks
      .filter((task) => !taken.has(`${task.source}:${task.id}`))
      .filter((task) => !words || `${task.title} ${task.board}`.toLowerCase().includes(words))
      .slice(0, 8);
    if (!hits.length) {
      const empty = document.createElement("p");
      empty.className = "empty";
      empty.textContent = tasks.length ? t("no task matches") : t("no open tasks. add one in tasks first");
      list.replaceChildren(empty);
      return;
    }
    list.replaceChildren(...hits.map((task) => {
      const pick = document.createElement("button");
      pick.type = "button";
      pick.className = "job-linker-pick";
      const badge = document.createElement("span");
      badge.className = `badge ${task.source}`;
      badge.textContent = task.source;
      const title = document.createElement("span");
      title.textContent = task.title;
      const board = document.createElement("small");
      board.textContent = task.board;
      pick.append(badge, title, board);
      pick.addEventListener("click", () => void saveLinks(job, [...job.links, { source: task.source, id: task.id, title: task.title, url: task.url }]));
      return pick;
    }));
  };
  search.oninput = paint;
  paint();
  search.focus();
}

// commissions whose payment panel is open, so a repaint keeps it open
const openPay = new Set<string>();

function jobCard(job: StudioJob, readOnly = false): HTMLElement {
  const client = job.client && job.client.toLowerCase() !== job.name.toLowerCase() ? t("for {client}", { client: job.client }) : "";
  const paid = job.paidAt && jobOwed(job) <= 0 && jobPrice(job) > 0 ? t("paid {day}", { day: paidDay(job) }) : "";
  // with a currency picked in setup, my amounts show in it whatever sign they were typed with
  // a commission set to its own currency shows in that
  const own = jobSign(job, "");
  const shown = own || (!readOnly ? mySign : "");
  const amount = shown && parseAmount(job.amount) ? formatMoney(parseAmount(job.amount), shown) : job.amount;
  const meta = [client, amount, !readOnly && jobPaid(job) && jobOwed(job) ? t("{owed} owed", { owed: formatMoney(jobOwed(job), shown || commonSymbol([job.amount])) }) : "", job.due && t("due {date}", { date: job.due }), readOnly && t(job.status), paid].filter(Boolean).join(" · ");
  const row = fillFields(cloneTemplate("template-job"), { name: job.name, meta: [job.typeName, meta].filter(Boolean).join(" · "), description: job.description });
  const pics = row.querySelector<HTMLElement>("[data-pics]")!;
  for (const url of job.imageUrls || []) {
    const image = document.createElement("img");
    image.loading = "lazy";
    image.decoding = "async";
    image.src = mediaSrc(url);
    image.alt = "";
    pics.append(image);
  }
  linkChips(row.querySelector<HTMLElement>("[data-links]")!, job, readOnly);
  // someone else's commission: status goes in the meta line, nothing to change
  if (readOnly) {
    row.classList.add("read-only");
    row.querySelector("[data-status]")?.remove();
    row.querySelector("[data-action='remove']")?.remove();
    row.querySelector("[data-action='link']")?.remove();
    row.querySelector("[data-action='pay']")?.remove();
    row.querySelector("[data-money]")?.remove();
    row.querySelector("[data-paypanel]")?.remove();
    return row;
  }
  row.classList.toggle("editing", editingJob?.id === job.id);
  // tapping the card itself, not one of its controls, opens it in the form
  row.addEventListener("click", (event) => {
    if ((event.target as Element).closest("button, select, input, label, a, .job-linker, .job-links")) return;
    startJobEdit(job);
  });
  const paidWrap = row.querySelector<HTMLElement>("[data-paid-wrap]")!;
  const paidField = row.querySelector<HTMLInputElement>("[data-paid]")!;
  paidWrap.hidden = !(jobPrice(job) > 0 && jobOwed(job) <= 0);
  paidField.value = job.paidAt || "";
  paidField.addEventListener("change", async () => {
    if (!paidField.value) return;
    render(await call(CHANNELS.studioAddJob, { ...job, paidAt: paidField.value, imageData: [] }));
  });
  paintMoney(row, job);
  const payKey = row.querySelector<HTMLElement>("[data-action='pay']")!;
  const panel = row.querySelector<HTMLElement>("[data-paypanel]")!;
  panel.hidden = !openPay.has(job.id);
  payKey.classList.toggle("active", !panel.hidden);
  payKey.addEventListener("click", () => {
    panel.hidden = !panel.hidden;
    payKey.classList.toggle("active", !panel.hidden);
    if (panel.hidden) openPay.delete(job.id);
    else openPay.add(job.id);
  });
  const payField = row.querySelector<HTMLInputElement>("[data-pay-amount]")!;
  row.querySelector("[data-pay-form]")!.addEventListener("submit", (event) => {
    event.preventDefault();
    void pay(job, payField.value, false);
  });
  row.querySelector("[data-pay-full]")!.addEventListener("click", () => void pay(job, jobOwed(job), false));
  row.querySelector("[data-pay-credit]")!.addEventListener("click", () => void pay(job, "", true));
  const linkKey = row.querySelector<HTMLElement>("[data-action='link']")!;
  const linker = row.querySelector<HTMLElement>("[data-linker]")!;
  if (!canUse("tasks", "edit")) linkKey.remove();
  linkKey.addEventListener("click", () => {
    linker.hidden = !linker.hidden;
    linkKey.classList.toggle("active", !linker.hidden);
    if (!linker.hidden) void fillLinker(row, job);
  });
  const status = row.querySelector<HTMLSelectElement>("[data-status]")!;
  for (const name of APP_SETTINGS.jobStatuses) {
    const option = document.createElement("option");
    option.value = name;
    option.textContent = t(name);
    status.append(option);
  }
  status.value = job.status || "sketch";
  status.addEventListener("change", async () => {
    render(await call(CHANNELS.studioAddJob, { ...job, status: status.value, imageData: [] }));
  });
  row.querySelector("[data-action='remove']")!.addEventListener("click", async () => {
    render(await call(CHANNELS.studioRemoveJob, job.id));
  });
  return row;
}

export async function setupStudioView(): Promise<void> {
  paintTimer();
  byId("studio-timer-start").addEventListener("click", startTimer);
  byId("studio-timer-stop").addEventListener("click", pauseTimer);
  byId("studio-timer-reset").addEventListener("click", resetTimer);
  byId("studio-timer").addEventListener("click", editTime);
  byId("studio-presets").addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLButtonElement>("[data-minutes]");
    if (!option) return;
    focusMinutes = Number(option.dataset.minutes) || 25;
    byId("studio-presets").querySelectorAll(".seg-option").forEach((item) => item.classList.toggle("active", item === option));
    resetTimer();
  });
  const picker = byId<HTMLInputElement>("studio-color-pick");
  picker.addEventListener("input", () => {
    byId<HTMLInputElement>("studio-color").value = picker.value;
  });

  byId("studio-color-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    const hex = byId<HTMLInputElement>("studio-color").value || picker.value;
    render(await call(CHANNELS.studioAddColor, hex));
    byId<HTMLInputElement>("studio-color").value = "";
  });
  byId("studio-prompt-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    const title = byId<HTMLInputElement>("studio-prompt-title").value;
    const text = byId<HTMLTextAreaElement>("studio-prompt-text").value;
    render(await call(CHANNELS.studioAddPrompt, title, text));
    byId<HTMLTextAreaElement>("studio-prompt-text").value = "";
  });
  byId("studio-job-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    const imageData = [];
    for (const file of newPics.slice(0, Math.max(0, 6 - keptPics.length))) imageData.push(await shrinkImage(file, APP_SETTINGS.sheetImageMaxPx));
    const editing = editingJob;
    try {
      render(await call(CHANNELS.studioAddJob, {
        ...(editing ? { ...(jobs.find((job) => job.id === editing.id) || editing), images: keptPics.map((pic) => pic.name) } : {}),
        name: byId<HTMLInputElement>("studio-job-name").value,
        client: byId<HTMLInputElement>("studio-job-client").value,
        due: byId<HTMLInputElement>("studio-job-due").value,
        amount: byId<HTMLInputElement>("studio-job-amount").value,
        currency: byId<HTMLSelectElement>("studio-job-currency").value,
        typeId: byId<HTMLSelectElement>("studio-job-type").value,
        status: byId<HTMLSelectElement>("studio-job-status").value,
        description: byId<HTMLTextAreaElement>("studio-job-description").value,
        imageData
      }));
    } catch (error) {
      flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
      return;
    }
    resetJobForm();
    byId<HTMLDetailsElement>("studio-job-fold").open = false;
    flashToast(editing ? t("commission saved") : t("commission added"));
  });
  // picking a type brings its price, which can still be typed over
  byId("studio-job-type").addEventListener("change", () => {
    const type = lastState.types.find((item) => item.id === byId<HTMLSelectElement>("studio-job-type").value);
    if (type?.price) byId<HTMLInputElement>("studio-job-amount").value = String(type.price);
  });
  byId("studio-rate-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    const editing = editingType;
    try {
      render(await call(CHANNELS.studioSaveType, {
        id: editing?.id,
        name: byId<HTMLInputElement>("studio-rate-name").value,
        price: byId<HTMLInputElement>("studio-rate-price").value,
        note: byId<HTMLInputElement>("studio-rate-note").value
      }));
    } catch (error) {
      flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
      return;
    }
    resetRateForm();
    byId<HTMLDetailsElement>("studio-rate-fold").open = false;
  });
  byId("studio-rate-cancel").addEventListener("click", () => {
    resetRateForm();
    byId<HTMLDetailsElement>("studio-rate-fold").open = false;
  });
  byId("studio-rate-starter").addEventListener("click", async () => render(await call(CHANNELS.studioStarterTypes)));
  byId("studio-credit-form").addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      render(await call(CHANNELS.studioAddCredit, byId<HTMLInputElement>("studio-credit-client").value, byId<HTMLInputElement>("studio-credit-amount").value, byId<HTMLInputElement>("studio-credit-note").value));
    } catch (error) {
      flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
      return;
    }
    byId<HTMLFormElement>("studio-credit-form").reset();
    byId<HTMLDetailsElement>("studio-credit-fold").open = false;
    flashToast(t("credit added"));
  });
  byId<HTMLInputElement>("studio-job-images").addEventListener("change", () => {
    const input = byId<HTMLInputElement>("studio-job-images");
    newPics = [...newPics, ...(input.files || [])].slice(0, Math.max(0, 6 - keptPics.length));
    input.value = "";
    paintJobPreview();
  });
  // "default" follows the sign from setup, the rest pin this commission to a currency
  const currencyPick = byId<HTMLSelectElement>("studio-job-currency");
  currencyPick.replaceChildren(Object.assign(document.createElement("option"), { value: "", textContent: t("default currency") }), ...CURRENCIES.filter((item) => item.sign).map((item) => Object.assign(document.createElement("option"), { value: item.id, textContent: item.label })));
  byId("studio-job-cancel").addEventListener("click", () => {
    resetJobForm();
    byId<HTMLDetailsElement>("studio-job-fold").open = false;
  });
  // the same setting as Setup > money, here so a phone sees it where the amounts are
  const globalCurrency = byId<HTMLSelectElement>("studio-currency");
  globalCurrency.replaceChildren(...CURRENCIES.map((item) => Object.assign(document.createElement("option"), { value: item.id, textContent: t(item.label) })));
  globalCurrency.addEventListener("change", async () => {
    try {
      rememberSettings(await call<UserSettings>(CHANNELS.settingsSave, { currency: globalCurrency.value }));
      window.dispatchEvent(new CustomEvent("chi-currency", { detail: globalCurrency.value }));
      flash(globalCurrency);
    } catch {
      flashToast(t("couldn't save that"));
    }
  });
  window.addEventListener("chi-currency", (event) => {
    globalCurrency.value = (event as CustomEvent<string>).detail || "auto";
  });
  void cachedSettings().then((settings) => {
    globalCurrency.value = settings.currency || "auto";
    mySign = currencySign(settings.currency, "");
    // the first paint can land before settings do, so repaint with the picked sign
    if (mySign) render(lastState);
  }).catch(() => undefined);
  window.addEventListener("chi-currency", (event) => {
    mySign = currencySign((event as CustomEvent<string>).detail, "");
    if (byId("view-studio").dataset.pane === "jobs") render(lastState);
  });

  void loadCommSign().then(() => {
    if (jobsView === "board") paintJobsView();
  });
  byId("jobs-view").addEventListener("click", (event) => {
    const next = (event.target as Element).closest<HTMLElement>("[data-view]")?.dataset.view === "board" ? "board" : "list";
    if (next === jobsView) return;
    jobsView = next;
    paintJobsView();
    void call(CHANNELS.studioView, next).catch(() => undefined);
  });
  byId("jobs-mode").addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLElement>("[data-mode]");
    const picked = option?.dataset.mode;
    if (picked === "money" || picked === "clients" || picked === "rates") setJobsMode(picked);
    else if (option) setJobsMode("list");
  });
  listen(CHANNELS.studioUpdated, async () => render(await call(CHANNELS.studioGet)));
  onViewShown("studio", async (detail) => {
    const pane = detail || "timer";
    byId("view-studio").dataset.pane = pane;
    document.querySelectorAll<HTMLElement>(".studio-pane").forEach((el) => {
      el.hidden = el.id !== `studio-pane-${pane}`;
    });
    if (pane === "timer") void showStudioTip();
    render(await call(CHANNELS.studioGet));
    if (pane === "jobs") void renderSharedJobs();
    const wanted = jobs.find((job) => job.id === editWanted);
    editWanted = "";
    if (wanted && pane === "jobs") startJobEdit(wanted);
  });
  onSharedChange("comms", () => {
    if (byId("view-studio").dataset.pane === "jobs") void renderSharedJobs();
  });
  document.getElementById("studio-pane-timer")!.hidden = false;
  document.addEventListener("visibilitychange", () => {
    if (ticking && document.visibilityState === "visible") tick();
  });
}
